import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { ScrollText, Loader2, RefreshCw, ShieldCheck, FileCheck2, Landmark, Home, Scale, BadgeCheck, Plus } from "lucide-react";

const CAT_META = {
  Permit: { icon: FileCheck2, tone: "text-sky-500" },
  Inspection: { icon: BadgeCheck, tone: "text-emerald-500" },
  Code: { icon: Scale, tone: "text-amber-500" },
  HUD: { icon: Landmark, tone: "text-purple-500" },
  HOA: { icon: Home, tone: "text-pink-500" },
  License: { icon: ShieldCheck, tone: "text-primary" },
  Other: { icon: ScrollText, tone: "text-muted-foreground" },
};

export default function CompliancePanel({ projectId }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [addingFees, setAddingFees] = useState(false);
  const { data, refetch } = useQuery({
    queryKey: ["compliance", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/compliance`)).data,
  });

  const run = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/compliance`);
      await refetch();
      toast.success("Compliance check generated");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const items = data?.items || [];
  const has = items.length > 0;
  const feeItems = items.filter((c) => c.applicable !== false && ["Permit", "Inspection"].includes(c.category) && Number(c.fee) > 0);
  const totalFees = feeItems.reduce((s, c) => s + Number(c.fee || 0), 0);
  const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

  const addFees = async () => {
    setAddingFees(true);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/compliance/add-fees`);
      qc.invalidateQueries({ queryKey: ["boq", projectId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(r.added ? `Added ${r.added} permit fee line${r.added > 1 ? "s" : ""} to the estimate` : "Permit fees already in the estimate");
    } catch (e) { toast.error(apiErr(e)); } finally { setAddingFees(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="compliance-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2"><ScrollText className="h-4 w-4 text-primary" /> Permits & Compliance</h3>
        <div className="flex items-center gap-2">
          {feeItems.length > 0 && (
            <button data-testid="add-permit-fees-btn" onClick={addFees} disabled={addingFees}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground px-2.5 py-1 text-xs font-semibold hover:bg-orange-600 disabled:opacity-60">
              {addingFees ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add fees ({money(totalFees)})
            </button>
          )}
          <button data-testid="gen-compliance-btn" onClick={run} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/50 text-primary px-2.5 py-1 text-xs font-semibold hover:bg-primary/10 disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {has ? "Re-check" : "Generate"}
          </button>
        </div>
      </div>

      {!has ? (
        <p className="text-sm text-muted-foreground">Generate a permits, inspections, code, HUD & HOA checklist for this project's location. Set the project location for jurisdiction-specific guidance.</p>
      ) : (
        <>
          {data.jurisdiction && <div className="text-xs text-muted-foreground mb-1">Jurisdiction: <span className="text-foreground font-medium">{data.jurisdiction}</span></div>}
          {data.summary && <p className="text-xs text-muted-foreground mb-3">{data.summary}</p>}
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="compliance-items">
            {items.map((c, i) => {
              const meta = CAT_META[c.category] || CAT_META.Other;
              const Icon = meta.icon;
              const na = c.applicable === false;
              return (
                <div key={i} className={`flex items-start gap-2 text-sm ${na ? "opacity-55" : ""}`} data-testid={`compliance-item-${i}`}>
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${meta.tone}`} />
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2">
                      <span className="truncate">{c.title}</span>
                      {Number(c.fee) > 0 && !na && <span className="shrink-0 font-mono text-xs text-primary">{money(c.fee)}</span>}
                      <span className="shrink-0 text-[9px] font-mono uppercase tracking-wider text-muted-foreground border border-border rounded px-1 py-0.5">{c.category}{na ? " · N/A" : ""}</span>
                    </div>
                    {c.detail && <div className="text-xs text-muted-foreground">{c.detail}</div>}
                    {c.authority && !na && <div className="text-[11px] text-muted-foreground/80 mt-0.5">Authority: {c.authority}</div>}
                  </div>
                </div>
              );
            })}
          </div>
          {data.disclaimer && <p className="text-[11px] italic text-muted-foreground mt-3 pt-3 border-t border-border">{data.disclaimer}</p>}
        </>
      )}
    </div>
  );
}
